"use server";

/** The runtime-built register document returns Boolean! (D34). */
type RegisterData = { register: boolean };

import { gql } from "@/lib/lecturehub/client";
import { GqlRequestError, friendlyMessage } from "@/lib/lecturehub/errors";
import { defaultLang, isLang, localeHeader } from "@/lib/i18n";
import { authReturn } from "@/lib/auth-return";
import { saveSignupReturn } from "@/lib/signup-return";
import { getDict } from "@/lib/i18n-server";
import { buildRegisterMutation } from "@/lib/lecturehub/register-fields";
import { loadRegisterExtraFields } from "@/lib/lecturehub/register-fields.server";
import type { RegisterState } from "./state";
import { loadPortalConfig } from "@/lib/lecturehub/portal-config";

export async function registerAction(
  _prev: RegisterState,
  formData: FormData,
): Promise<RegisterState> {
  const email = String(formData.get("email") ?? "").trim();
  const password = String(formData.get("password") ?? "");
  const repeatPassword = String(formData.get("repeat_password") ?? "");
  const name = String(formData.get("name") ?? "").trim();
  const surname = String(formData.get("surname") ?? "").trim();
  const newsletter = formData.get("newsletter") === "on";
  const terms = formData.get("terms") === "on";
  const gdpr = formData.get("gdpr") === "on";

  // Locale is the only piece of UI context the action gets — pulled from a
  // hidden form input so error copy can match the page the user is on. Any
  // enabled locale works: getDict overlays the portal translations, and an
  // unmapped/empty value falls back to the portal default inside dict().
  const rawLang = String(formData.get("lang") ?? "");
  const lang = isLang(rawLang) ? rawLang : defaultLang();
  const next = authReturn(lang, String(formData.get("redirect") ?? ""));
  const t = (await getDict(lang)).auth;
  // The portal's registration policy (O18). Re-read here, not trusted from the
  // form: a Server Action is its own entry point.
  const { registration } = await loadPortalConfig();
  if (!registration.enabled) return { ok: false, error: t.registrationClosed };

  if (!email || !password || !name || !surname) {
    return { ok: false, error: t.allFieldsRequired };
  }
  if (password !== repeatPassword) {
    return {
      ok: false,
      errors: { repeat_password: [t.passwordMismatch] },
    };
  }
  // Each consent box exists only when the portal asks for it; the backend
  // mutation still takes both flags, so an un-asked consent is sent as false.
  if (registration.requireTerms && !terms) {
    return { ok: false, error: t.termsRequired };
  }
  if (registration.requireGdpr && !gdpr) {
    return { ok: false, error: t.gdprRequired };
  }

  // The portal's extra register arguments (O44) — re-read from the schema, not
  // trusted from the form. Their values ride along; a required one left empty is
  // a field error (the browser's `required` normally catches it first).
  const extras = await loadRegisterExtraFields();
  const extraValues: Record<string, string | boolean> = {};
  for (const f of extras) {
    if (f.kind === "Boolean") {
      extraValues[f.name] = formData.get(f.name) === "on";
      continue;
    }
    const value = String(formData.get(f.name) ?? "").trim();
    if (f.required && !value) return { ok: false, errors: { [f.name]: [t.allFieldsRequired] } };
    if (value) extraValues[f.name] = value;
  }

  try {
    await gql<RegisterData, Record<string, unknown>>(
      buildRegisterMutation(extras),
      {
        email,
        password,
        repeat_password: repeatPassword,
        name,
        surname,
        newsletter,
        terms,
        gdpr,
        ...extraValues,
      },
      // X-Locale so backend validation messages come back in the page's
      // language (they surface verbatim via err.validation()/friendlyMessage).
      { asGuest: true, locale: localeHeader(lang), cache: "no-store" },
    );
    await saveSignupReturn(lang, next);
    return { ok: true, email, pending: registration.type === "admin_confirmation" };
  } catch (err) {
    const errors = (await getDict(lang)).errors;
    if (err instanceof GqlRequestError) {
      const v = err.validation();
      if (v) return { ok: false, errors: v };
      // The one register-specific code the backend throws (ACCOUNT_EXISTS):
      // it is about the email, so it belongs on that field — the page footer
      // already offers the sign-in link.
      if (err.hasCode("ACCOUNT_EXISTS"))
        return { ok: false, errors: { email: [errors.accountExists] } };
    }
    return { ok: false, error: friendlyMessage(err, errors) };
  }
}
